import type { FeaturesResponse, GlobalPermission, MeResponse } from '@pm-tool/shared';
import { notFound } from '@tanstack/react-router';

type AdminSection = {
  permission: GlobalPermission;
  to: string;
  /** Only while these feature flags are on for the user. */
  feature?: keyof FeaturesResponse['flags'] | readonly (keyof FeaturesResponse['flags'])[];
};

export const adminSections = [
  { permission: 'admin.users', to: '/admin/users' },
  { permission: 'admin.organizations', to: '/admin/organizations' },
  { permission: 'admin.roles', to: '/admin/roles' },
  { permission: 'job_roles.manage', to: '/admin/resources', feature: 'resources' },
  { permission: 'job_roles.manage', to: '/admin/job-roles', feature: 'resources' },
  {
    permission: 'job_roles.manage',
    to: '/admin/skills',
    feature: ['resources', 'skills_and_areas'],
  },
  { permission: 'admin.audit', to: '/admin/audit', feature: 'audit_log' },
  { permission: 'admin.system', to: '/admin/system', feature: 'system_overview' },
] as const satisfies readonly AdminSection[];

type Flags = FeaturesResponse['flags'] | undefined;

/** Sections the user may open; sections behind a flag stay hidden while flags are loading. */
export function visibleAdminSections(me: MeResponse, flags: Flags) {
  return adminSections.filter(
    (s: AdminSection) =>
      me.globalPermissions.includes(s.permission) &&
      (s.feature === undefined ||
        (typeof s.feature === 'string' ? [s.feature] : s.feature).every(
          (flag) => flags?.[flag] === true,
        )),
  );
}

export function hasAnyAdminSection(me: MeResponse, flags: Flags): boolean {
  return visibleAdminSections(me, flags).length > 0;
}

/**
 * Route guard (beforeLoad): without the permission the page does not exist for the user. Only
 * routing convenience – the API answers 403 anyway.
 */
export function requirePermission(me: MeResponse, permission: GlobalPermission): void {
  if (!me.globalPermissions.includes(permission)) throw notFound();
}
